/**
 * Telemetria cliente — PostHog (client-side).
 *
 * Por que client-side além do server:
 *   - Page views, cliques, scrolls, time-on-page só existem no browser.
 *   - Captura sessão completa (replay opcional pra debug de UX).
 *   - Dashboards web vitals + funnels de UI.
 *
 * Política:
 *   - SEM SDK quando ausência de chave — funções viram no-op.
 *   - Import dinâmico do posthog-js: bundle não cresce em dev/staging.
 *   - Sanitize automático de props (mesma lista do server).
 *   - Capture com `tenant_id` como group pra alinhar com backend.
 *
 * Setup: chamar `initTelemetry()` em main.tsx (uma vez). Em seguida
 * `identifyUser(...)` após login bem-sucedido. Eventos via
 * `captureEvent(name, props)`.
 */

type PostHogClient = {
  init: (key: string, config: Record<string, unknown>) => void;
  identify: (id: string, props?: Record<string, unknown>) => void;
  capture: (event: string, props?: Record<string, unknown>) => void;
  reset: () => void;
  group: (type: string, key: string, props?: Record<string, unknown>) => void;
};

let posthog: PostHogClient | null = null;
let initialized = false;

/**
 * Inicializa PostHog se VITE_POSTHOG_KEY estiver setado. No-op
 * silencioso senão — perfeito pra dev local.
 */
export async function initTelemetry(): Promise<void> {
  if (initialized) return;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) {
     
    console.info('[telemetry] VITE_POSTHOG_KEY ausente — captura cliente desabilitada');
    initialized = true;
    return;
  }
  try {
    // Import dinâmico — bundler não inclui posthog-js quando key não
    // está setada (dead code elimination).
     
    // @ts-ignore - dep opcional em tempo de tipagem
    const mod = await import('posthog-js');
    posthog = (mod.default ?? mod) as PostHogClient;
    posthog.init(key, {
      api_host: (import.meta.env.VITE_POSTHOG_HOST as string) ?? 'https://us.i.posthog.com',
      // Privacy-first: nada de session recording sem opt-in.
      disable_session_recording: true,
      // Captura page views automaticamente (SPA: chamada explícita
      // no router quando rota muda — ver useTelemetryPageView).
      capture_pageview: false,
      // Auto-capture cliques em data-testid + links — leve.
      autocapture: { dom_event_allowlist: ['click', 'submit'] },
      persistence: 'localStorage',
    });
    initialized = true;
  } catch (err) {
     
    console.warn('[telemetry] posthog-js falhou ao carregar:', err);
    initialized = true; // não tenta de novo
  }
}

export function identifyUser(args: {
  userId: string;
  email: string;
  role: string;
  tenantId: string;
}): void {
  if (!posthog) return;
  posthog.identify(`user:${args.userId}`, {
    email: args.email,
    role: args.role,
    tenant_id: args.tenantId,
  });
  // Group analytics — todos os eventos seguintes ficam associados ao
  // tenant. Dashboards conseguem agrupar por clínica.
  posthog.group('tenant', args.tenantId);
}

export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!posthog) return;
  posthog.capture(event, sanitize(properties ?? {}));
}

/**
 * Page view manual — chame em useEffect quando location muda.
 * Centralizamos aqui pra padronizar property naming.
 */
export function capturePageView(path: string): void {
  captureEvent('$pageview', { $current_url: path });
}

/**
 * Reset no logout — limpa distinct ID e cookies.
 */
export function resetTelemetry(): void {
  if (!posthog) return;
  posthog.reset();
}

/**
 * Sanitiza props removendo qualquer key que cheire PHI/segredo +
 * flattens valores pra primitivos. Espelha o backend.
 */
function sanitize(
  props: Record<string, unknown>,
): Record<string, unknown> {
  const banned = new Set([
    'password',
    'token',
    'accesstoken',
    'refreshtoken',
    'cpf',
    'fullname',
    'phone',
    'rawtext',
    'notes',
  ]);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (banned.has(k.toLowerCase().replace(/[_-]/g, ''))) continue;
    if (v === null || v === undefined) continue;
    if (
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean'
    ) {
      out[k] = v;
    } else if (Array.isArray(v)) {
      out[k] = `[array len=${v.length}]`;
    } else if (typeof v === 'object') {
      out[k] = `[object keys=${Object.keys(v as object).length}]`;
    }
  }
  return out;
}
